<template>
    <div
        :class="['schedule-appointment', 'v2', styleTheme]"
        :style="injectRootVar"
    >
        <div class="schedule-title">
            <span>{{ tourType.title }}</span>
            <span v-show="tourType.show">
                <span class="iconfont question-mark tour-type-tip"></span>
                <span class="tip" v-text="tourType.tip"></span>
            </span>
        </div>
        <div class="booking-tab" v-show="tourType.show">
            <span
                v-for="(label, idx) in tourType.tabs"
                :key="idx + ''"
                class="tab-item"
                :class="{ active: tourTypeIdx == idx }"
                @click="selectTourType(idx)"
            >{{ label }}
            </span>
        </div>
        <div class="booking-date-wrap">
            <span
                class="iconfont icon-arrow_08_left booking-prev disabled"
            ></span>
            <Swiper
                class="booking-date-content swiper-container"
                v-bind="swiperOption"
                @init="onSwiperInit"
            >
                <SwiperSlide
                    v-for="(date, index) in dayList"
                    :key="date.time"
                    class="date"
                    :class="{
                        selected: selectedDateIndex == index,
                        disabled: !date.available.length
                    }"
                    @click="onDayChange(date, index)"
                >
                    <div class="week">{{ date.week }}</div>
                    <div class="date-day">{{ date.day }}</div>
                    <div class="month">{{ date.month }}</div>
                </SwiperSlide>
            </Swiper>
            <span class="iconfont icon-arrow_08_right booking-next"></span>
        </div>
        <div v-if="inPop" class="booking-time">
            <p>{{ $t('md-detail-info.tourStartTime') }}</p>
            <Select
                class="start-time"
                v-model="timeRange.start"
                @change="onStartChange()"
                :options="startList"
                valueKey="id"
                labelKey="name"
                placeholder="Please Select Start Time"
            />
            <p>{{ $t('md-detail-info.tourEndTime') }}</p>
            <Select
                class="end-time"
                v-model="timeRange.duration"
                @change="onEndChange()"
                :options="endList"
                valueKey="id"
                labelKey="name"
                placeholder="Please Select End Time"
            />
        </div>
        <div class="btn-wrap">
            <button
                class="schedule-button"
                :class="scheduleBtnClassForGA"
                @click="submitTour()"
            >
                {{ $t('md-detail-info.scheduleATour') }}
            </button>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from '@/store/helpers';
import { Swiper, SwiperSlide, fixSwiperOptions } from '@/common/swiper';
import { scheduleAppointment } from './schedule';
import {
    fifteenMin,
    getStartList,
    getEndList,
} from './schedule-appointment.js';
import Select from './_components/select.vue';
import util from '@/common/util';
import { calcColorRootVar } from '@/init/data/handlerData.js';
export default {
    emits: ['change'],
    i18next: { ns: 'md-detail-info' },
    inject: ['schedulePop', 'colorMap'],
    props: {
        inPop: { type: Boolean, default: false },
        swiperConfig: { type: Object, default: () => ({}) }
    },
    mixins: [scheduleAppointment],
    data() {
        return {
            day: null,
            timeRange: {
                start: 0, //timestamp
                duration: 0, // (endtimestamp - starttimestamp )/15min
                right: 0, // right threshold of this available time period
                maxLength: 0 // durationType / 15min
            }
        };
    },
    computed: {
        ...mapState({
            listingDetail: 'listingDetail',
            showingConfig: 'showingConfig',
            dayList: 'dayList'
        }),
        swiperOption({ swiperConfig = {} }) {
            let { slidesPerView = 3 } = swiperConfig;
            let options = {
                slidesPerView,
                spaceBetween: 10,
                slidesPerGroup: slidesPerView,
                navigation: {
                    closest: '.booking-date-wrap',
                    prevEl: '.booking-prev',
                    nextEl: '.booking-next',
                    disabledClass: 'disabled'
                }
            };
            Object.assign(options, swiperConfig);
            return fixSwiperOptions(options);
        },
        injectRootVar() {
            return calcColorRootVar.call(this, {
                colors: [
                    '--g-btn-background_10',
                    '--g-primary-color_90',
                    '--g-tip-color_60'
                ]
            });
        },
        startList({ day }) {
            return getStartList(day);
        },
        endList({ timeRange }) {
            let ret = getEndList(timeRange);
            return ret;
        }
    },
    watch: {
        'statusStore.selectedDateIndex'(newVal) {
            this.onSlideChange(newVal);
        }
    },
    async created() {
        await this.onLngNsLoaded('common');
        this.updateDayList();
        this.init();
    },
    methods: {
        ...mapActions({
            updateDayList: 'updateDayList'
        }),
        onSwiperInit(swiper) {
            this.swiper = swiper;
        },
        onSlideChange(index) {
            if (!util.isSSRServer && this.swiper) {
                this.swiper.slideTo(index);
            }
        },
        init() {
            this.timeRange.maxLength =
                this.showingConfig.durationType / 15 || 2;
            let {
                selectedDateIndex,
                appointmentStartTime,
                appointmentEndTime
            } = this.statusStore;
            let item = null;
            if (appointmentStartTime) {
                this.day = this.dayList[selectedDateIndex];
                item = this.startList.find(v => v.id === appointmentStartTime);
            }
            if (item) {
                this.timeRange.start = appointmentStartTime;
                this.timeRange.right = item.right;
                this.timeRange.duration =
                    (appointmentEndTime - appointmentStartTime) / fifteenMin;
            } else {
                this.chooseFirstAvailablePeriod(selectedDateIndex);
            }
        },
        chooseFirstAvailablePeriod(idx = 0) {
            for (let i = idx; i < this.dayList.length; i++) {
                const day = this.dayList[i];
                if (!day.available.length) {
                    continue;
                }
                this.onDayChange(day, i);
                break;
            }
        },
        onDayChange(day, index) {
            if (!day.available.length || day == this.day) {
                return;
            }
            this.day = day;
            this.onStartChange(day.available[0][0]);
            this.updateScheduleTour({ selectedDateIndex: index });
            this.onSlideChange(index);
        },
        onStartChange(start) {
            if (!start) {
                start = this.timeRange.start;
            }
            let item = this.startList.find(v => v.id === start);
            let { right } = item;

            this.timeRange.start = start;
            this.timeRange.right = right;
            let duration = Math.min(right, this.timeRange.maxLength, 2);
            this.timeRange.duration = duration;
            this.$emit('change', [start, start + duration * fifteenMin]);
        },
        onEndChange() {
            const start = this.timeRange.start;
            if (!start) {
                return; 
            }
            let right = this.timeRange.duration;
            this.$emit('change', [start, start + right * fifteenMin]);
        },
        async submitTour() {
            if (this.inPop) {
                this.submitForm();
            } else {
                this.schedulePop();
            }
        }
    },
    components: { Swiper, SwiperSlide, Select }
};
</script>
<style lang="scss">
@import '@/theme/mixin.scss';
@import '@/theme/media.scss';

.schedule-appointment {
    &.fashion {
        --g-primary-color_90: rgba(255, 255, 255, 0.9) !important;
    }
    .schedule-title {
        position: relative;
        color: var(--g-primary-color);
        font-family: var(--font-title);
        text-align: center;
        font-weight: 700;
        font-size: 20px;
        line-height: 1.5;
        margin-bottom: 20px;

        @include media-800 {
            font-size: 16px;
        }

        .tip {
            display: none;
            position: absolute;
            top: calc(100% + 4px);
            left: 20px;
            right: 20px;
            z-index: 10;
            font-family: var(--font-normal);
            color: var(--g-bg-color);
            background-color: var(--g-primary-color_90);
            padding: 15px 10px 10px;
            font-size: 12px;
            line-height: 1.5;
            font-weight: 500;
            box-shadow: 0 2px 5px 0 rgba(0, 10, 30, 0.1);
        }
        .tour-type-tip {
            position: relative;
            margin-left: 4px;
            z-index: 1;
            cursor: pointer;
            &.question-mark {
                text-align: center;
                font-size: 12px;
                line-height: 1.1;
                vertical-align: middle;
                &::before {
                    content: '?';
                    border: 1px solid;
                    border-radius: 50%;
                    display: inline-block;
                    width: 14px;
                    height: 14px;
                    margin-bottom: 3px;
                    color: var(--g-bg-color);
                    background: var(--g-tip-color_60);
                }
                &:hover::before {
                    background: var(--g-tip-color);
                }
            }
            &::after {
                display: none;
                content: '\e79b';
                font-family: 'iconfont';
                position: absolute;
                width: 10px;
                height: 10px;
                top: 16px;
                left: 2px;
                color: var(--g-primary-color_90);
                z-index: 10;
            }
            &:hover {
                &::after {
                    display: block;
                }
            }
        }
        .tour-type-tip:hover + .tip {
            display: block;
        }
    }
    .booking-tab {
        display: flex;
        .tab-item {
            flex: 1;
            color: var(--g-text-color);
            cursor: pointer;
            line-height: 32px;
            text-align: center;
            border: 1px solid var(--color-border);
            border-left-width: 0;
            &:first-of-type {
                border-left-width: 1px;
                border-right-width: 0px;
            }

            &.active {
                font-weight: 700;
                color: var(--g-primary-color);
                border: 1px solid var(--g-btn-background);
                background-color: var(--g-btn-background_10);
            }
        }
    }
    .booking-date-wrap {
        display: flex;
        overflow: hidden;
        margin: 25px -5px 20px;
    }
    .booking-prev,
    .booking-next {
        flex-shrink: 0;
        font-size: 14px;
        color: var(--g-primary-color);
        cursor: pointer;
        padding: 0 5px;
        line-height: 94px;
        user-select: none;
        &.disabled {
            opacity: 0.3;
            cursor: not-allowed;
            color: var(--g-primary-color);
        }
        &:hover {
            color: var(--g-btn-background);
        }
    }
    .booking-date-content {
        width: 100%;
        overflow: hidden;
    }
    .date {
        height: 94px;
        cursor: pointer;
        color: var(--g-text-color);
        border: 1px solid rgba(218, 218, 218, 0.5);
        padding: 15px 0;
        text-align: center;
        line-height: 1.25;

        &:hover,
        &.selected {
            border-color: var(--g-btn-background);
            background-color: var(--g-btn-background_10);
        }
        &.disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
        .week {
            font-size: 12px;
        }
        .date-day {
            font-size: 20px;
            color: var(--g-primary-color);
            font-weight: 700;
            margin: 5px 0;
        }
    }

    select {
        font-size: 14px;
        width: 100%;
        height: 40px;
        padding-left: 10px;
        font-family: var(--font-normal);
        box-shadow: none;
        border: 1px solid var(--color-border);
        cursor: pointer;
    }
    .booking-time {
        padding-top: 5px;
        & > p {
            color: var(--g-primary-color);
            line-height: 1.2;
            margin-bottom: 10px;
            font-weight: 500;
        }
        .base-select {
            margin-bottom: 20px;
        }
    }
    .btn-wrap {
        width: 100%;
    }
    .schedule-button {
        width: 100%;
        height: 40px;
        font-weight: 700;
        font-size: 16px;
        margin-top: 10px;
        @include color-button-v2();
        @include color-button-hover();
    }
}
</style>
